import type { AntSimWorld, AntState, FoodField } from './contracts';
import type { Terrain } from '../game/Terrain';
import { resolveObstacles, type Obstacle } from './Collision';

export interface SpiderState {
  x: number;
  y: number;
  z: number;
  heading: number;
  speed: number;
  task: 'patrol' | 'chase' | 'eat';
  targetAntId: number;
  eatTimer: number;
  alive: boolean;
  respawnT: number;  // seconds until respawn when !alive
  /** Burst-move state — spiders skitter for a moment, then sit still. */
  moveState: 'dash' | 'still';
  moveT: number;     // seconds remaining in current moveState
  /** Seconds of backstep remaining — set when an ant bites, drives a short
   *  reverse walk before the spider resumes its skitter. */
  backstepT: number;
  /** Surface Y the spider was at when killed — we sink from this baseline. */
  deathY: number;
  /** Seconds since death. Drives the slow-sink animation and marks when the
   *  corpse has turned into food. */
  sinkT: number;
  /** Set once per death so we only drop one corpse-food pile. */
  foodDropped: boolean;
}

export interface SpiderSimOptions {
  count?: number;
  patrolRadius?: number;
  /** Static circular obstacles (rocks + grass clusters) the spider must go around. */
  obstacles?: readonly Obstacle[];
  /** Optional callback returning nearby obstacles at (x,z) — used for the grass
   *  spatial grid so we don't scan all 4 k tufts every tick. */
  nearbyObstacles?: (x: number, z: number) => readonly Obstacle[];
  /** Food field — corpses drop into this when a spider dies + sinks. */
  food?: FoodField;
}

const PATROL_DASH_SPEED = 0.55;   // burst speed during a skitter
const CHASE_DASH_SPEED = 0.95;    // faster when hunting
const PATROL_DASH_MIN = 0.25;     // seconds
const PATROL_DASH_MAX = 0.7;
const PATROL_STILL_MIN = 1.0;
const PATROL_STILL_MAX = 2.6;
const CHASE_DASH_MIN = 0.35;
const CHASE_DASH_MAX = 0.9;
const CHASE_STILL_MIN = 0.12;
const CHASE_STILL_MAX = 0.45;
const SCAN_RADIUS = 3;
const CHASE_GIVEUP = 6;
const KILL_RADIUS = 0.3;
const EAT_DURATION = 2.5;
const HEADING_NOISE_INTERVAL = 1.0;
const SWARM_RADIUS = 1.5;         // mobbing perimeter
const SWARM_KILL_COUNT = 15;      // ants within SWARM_RADIUS to kill spider
const RESPAWN_DELAY = 30;         // seconds
const BITE_R = 0.45;              // an ant this close counts as biting the spider
const BITE_R2 = BITE_R * BITE_R;
const BACKSTEP_DURATION = 0.35;   // seconds of reverse scuttle per bite
const BACKSTEP_COOLDOWN = 1.2;    // don't retrigger every frame
const BACKSTEP_SPEED = 0.45;      // metres / second reverse
/** Total lifetime of the corpse before it disappears + drops food (≈3 in-game
 *  hours at DAY_LENGTH = 600 s). The body lies still for the first SIT_BEFORE
 *  seconds, then slowly sinks into the ground over the remaining time. */
const SINK_DURATION = 75;
const SIT_BEFORE_SINK = 60;       // seconds the corpse sits before sinking
const SINK_DEPTH = 0.45;          // how far below the surface the corpse settles

const SCAN_RADIUS_SQ = SCAN_RADIUS * SCAN_RADIUS;
const GIVEUP_SQ = CHASE_GIVEUP * CHASE_GIVEUP;
const KILL_SQ = KILL_RADIUS * KILL_RADIUS;

export class SpiderSim {
  readonly spiders: SpiderState[] = [];
  private readonly headingTimers: number[] = [];
  private readonly patrolRadius: number;
  private readonly obstacles: readonly Obstacle[];
  private readonly nearbyObstacles: ((x: number, z: number) => readonly Obstacle[]) | null;
  private readonly food: FoodField | null;

  constructor(
    private terrain: Terrain,
    private world: AntSimWorld,
    opts: SpiderSimOptions = {},
  ) {
    const count = opts.count ?? 2;
    this.patrolRadius = opts.patrolRadius ?? 30;
    this.obstacles = opts.obstacles ?? [];
    this.nearbyObstacles = opts.nearbyObstacles ?? null;
    this.food = opts.food ?? null;

    for (let i = 0; i < count; i++) {
      const a = Math.random() * Math.PI * 2;
      const r = Math.random() * this.patrolRadius;
      const x = Math.cos(a) * r;
      const z = Math.sin(a) * r;
      this.spiders.push({
        x,
        y: terrain.getHeight(x, z),
        z,
        heading: Math.random() * Math.PI * 2,
        speed: 0,
        task: 'patrol',
        targetAntId: -1,
        eatTimer: 0,
        alive: true,
        respawnT: 0,
        moveState: 'still',
        moveT: PATROL_STILL_MIN + Math.random() * (PATROL_STILL_MAX - PATROL_STILL_MIN),
        backstepT: 0,
        deathY: 0,
        sinkT: 0,
        foodDropped: false,
      });
      this.headingTimers.push(Math.random() * HEADING_NOISE_INTERVAL);
    }
  }

  update(dt: number): void {
    const ants = this.world.ants;

    for (let i = 0; i < this.spiders.length; i++) {
      const s = this.spiders[i];

      // Dead spider — slowly sink below the surface over SINK_DURATION
      // (~3 in-game hours), drop a corpse-food pile at that time, then
      // respawn a fresh spider elsewhere after the respawn cooldown.
      if (!s.alive) {
        s.sinkT += dt;
        s.respawnT -= dt;
        const terrainY = this.terrain.getHeight(s.x, s.z);
        // Lie still on the ground until SIT_BEFORE_SINK, then sink over the
        // remaining time. Y stays at terrain level throughout the sit phase.
        const sinkTail = Math.max(0.001, SINK_DURATION - SIT_BEFORE_SINK);
        const sinkFrac = Math.max(0, Math.min(1, (s.sinkT - SIT_BEFORE_SINK) / sinkTail));
        s.y = terrainY - sinkFrac * SINK_DEPTH;
        if (!s.foodDropped && s.sinkT >= SINK_DURATION && this.food) {
          // Wolf-spider corpse is sizeable food for the colony.
          this.food.spawn(s.x, s.z, 6, 12, 'corpse');
          s.foodDropped = true;
        }
        if (s.respawnT <= 0) {
          const a = Math.random() * Math.PI * 2;
          const r = (0.4 + Math.random() * 0.6) * this.patrolRadius;
          s.x = Math.cos(a) * r;
          s.z = Math.sin(a) * r;
          s.y = this.terrain.getHeight(s.x, s.z);
          s.heading = Math.random() * Math.PI * 2;
          s.speed = 0;
          s.task = 'patrol';
          s.moveState = 'still';
          s.moveT = PATROL_STILL_MIN + Math.random() * (PATROL_STILL_MAX - PATROL_STILL_MIN);
          s.targetAntId = -1;
          s.eatTimer = 0;
          s.alive = true;
          s.respawnT = 0;
          s.sinkT = 0;
          s.foodDropped = false;
          s.backstepT = 0;
        }
        continue;
      }

      // Bite detection — any alive ant within BITE_R triggers a brief
      // backstep (reverse walk) unless we just did one.
      if (s.backstepT <= -BACKSTEP_COOLDOWN) {
        for (let j = 0; j < ants.length; j++) {
          const a = ants[j];
          if (!a.alive || a.nestCycleT > 0) continue;
          const dx = a.x - s.x;
          const dz = a.z - s.z;
          if (dx * dx + dz * dz < BITE_R2) {
            s.backstepT = BACKSTEP_DURATION;
            break;
          }
        }
      }
      if (s.backstepT > -BACKSTEP_COOLDOWN) s.backstepT -= dt;

      // Swarm check — count alive ants in mobbing range. If ≥ threshold, the
      // spider is overwhelmed and dies. Respawn after a cooldown.
      let mob = 0;
      for (let j = 0; j < ants.length; j++) {
        const a = ants[j];
        if (!a.alive || a.nestCycleT > 0) continue;
        const dx = a.x - s.x;
        const dz = a.z - s.z;
        if (dx * dx + dz * dz < SWARM_RADIUS * SWARM_RADIUS) mob++;
      }
      if (mob >= SWARM_KILL_COUNT) {
        s.alive = false;
        s.respawnT = RESPAWN_DELAY;
        s.speed = 0;
        s.task = 'patrol';
        s.targetAntId = -1;
        continue;
      }

      if (s.task === 'eat') {
        s.speed = 0;
        s.eatTimer -= dt;
        if (s.eatTimer <= 0) {
          s.task = 'patrol';
          s.targetAntId = -1;
        }
        s.y = this.terrain.getHeight(s.x, s.z);
        continue;
      }

      if (s.task === 'patrol') {
        // Scan for a nearby ant; transition into chase if one's close.
        let bestId = -1;
        let bestDistSq = SCAN_RADIUS_SQ;
        for (let j = 0; j < ants.length; j++) {
          const a = ants[j];
          if (!a.alive) continue;
          const dx = a.x - s.x;
          const dz = a.z - s.z;
          const dSq = dx * dx + dz * dz;
          if (dSq < bestDistSq) {
            bestDistSq = dSq;
            bestId = a.id;
          }
        }
        if (bestId >= 0) {
          s.task = 'chase';
          s.targetAntId = bestId;
          s.moveState = 'dash';
          s.moveT = CHASE_DASH_MIN + Math.random() * (CHASE_DASH_MAX - CHASE_DASH_MIN);
        } else {
          // Burst-step wander: alternating dash + still.
          s.moveT -= dt;
          if (s.moveT <= 0) {
            if (s.moveState === 'still') {
              s.moveState = 'dash';
              s.moveT = PATROL_DASH_MIN + Math.random() * (PATROL_DASH_MAX - PATROL_DASH_MIN);
              s.heading += (Math.random() - 0.5) * 1.8; // pick a new scurry direction
            } else {
              s.moveState = 'still';
              s.moveT = PATROL_STILL_MIN + Math.random() * (PATROL_STILL_MAX - PATROL_STILL_MIN);
            }
          }
          s.speed = s.moveState === 'dash' ? PATROL_DASH_SPEED : 0;
        }
      }

      if (s.task === 'chase') {
        const target = this.findAnt(s.targetAntId);
        if (!target || !target.alive) {
          s.task = 'patrol';
          s.targetAntId = -1;
          s.speed = 0;
          s.moveState = 'still';
          s.moveT = PATROL_STILL_MIN + Math.random() * (PATROL_STILL_MAX - PATROL_STILL_MIN);
        } else {
          const dx = target.x - s.x;
          const dz = target.z - s.z;
          const dSq = dx * dx + dz * dz;
          if (dSq > GIVEUP_SQ) {
            s.task = 'patrol';
            s.targetAntId = -1;
            s.speed = 0;
            s.moveState = 'still';
            s.moveT = PATROL_STILL_MIN + Math.random() * (PATROL_STILL_MAX - PATROL_STILL_MIN);
          } else if (dSq < KILL_SQ) {
            target.alive = false;
            s.task = 'eat';
            s.eatTimer = EAT_DURATION;
            s.speed = 0;
            s.y = this.terrain.getHeight(s.x, s.z);
            continue;
          } else {
            // Chase in short aggressive skitters.
            s.moveT -= dt;
            if (s.moveT <= 0) {
              if (s.moveState === 'still') {
                s.moveState = 'dash';
                s.moveT = CHASE_DASH_MIN + Math.random() * (CHASE_DASH_MAX - CHASE_DASH_MIN);
              } else {
                s.moveState = 'still';
                s.moveT = CHASE_STILL_MIN + Math.random() * (CHASE_STILL_MAX - CHASE_STILL_MIN);
              }
            }
            s.heading = Math.atan2(dz, dx);
            s.speed = s.moveState === 'dash' ? CHASE_DASH_SPEED : 0;
          }
        }
      }

      // Active backstep overrides normal forward skitter — slow reverse walk
      // in the OPPOSITE direction of heading, keeping facing the same so the
      // walking-backward animation reads correctly.
      if (s.backstepT > 0) {
        s.x -= Math.cos(s.heading) * BACKSTEP_SPEED * dt;
        s.z -= Math.sin(s.heading) * BACKSTEP_SPEED * dt;
      } else {
        s.x += Math.cos(s.heading) * s.speed * dt;
        s.z += Math.sin(s.heading) * s.speed * dt;
      }

      // Hard-edge obstacle pushout: rocks + local grass. `nearbyObstacles`
      // bundles the spatial-hash lookup so we only test ~9 cells of grass.
      if (this.obstacles.length > 0) resolveObstacles(s, 0.08, this.obstacles);
      if (this.nearbyObstacles) resolveObstacles(s, 0.08, this.nearbyObstacles(s.x, s.z));

      const r = Math.hypot(s.x, s.z);
      if (r > this.patrolRadius) {
        const k = this.patrolRadius / r;
        s.x *= k;
        s.z *= k;
        s.heading += Math.PI;
      }

      s.y = this.terrain.getHeight(s.x, s.z);
    }
  }

  /** Instant-kill any alive spider within `radius` of (cx, cz). Respawn clock
   *  reuses the standard post-death delay. */
  killArea(cx: number, cz: number, radius: number): number {
    const r2 = radius * radius;
    let killed = 0;
    for (let i = 0; i < this.spiders.length; i++) {
      const s = this.spiders[i];
      if (!s.alive) continue;
      const dx = s.x - cx;
      const dz = s.z - cz;
      if (dx * dx + dz * dz > r2) continue;
      s.alive = false;
      s.respawnT = RESPAWN_DELAY;
      s.speed = 0;
      s.task = 'patrol';
      s.targetAntId = -1;
      killed++;
    }
    return killed;
  }

  private findAnt(id: number): AntState | undefined {
    if (id < 0) return undefined;
    const ants = this.world.ants;
    for (let i = 0; i < ants.length; i++) {
      if (ants[i].id === id) return ants[i];
    }
    return undefined;
  }
}
